import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faPlus, faXmark, faUser, faVideo, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import useUserSearch from '../../../hooks/useUserSearch';
import { useTranslation } from "react-i18next";
import { canJoinCastOrCrew } from '../../../utils/epkRoles';

export default function AddCastCrewModal({
  isOpen,
  onClose,
  currentCast = [],
  currentCrew = [],
  ownerId,
  onAddMember,
  onRemoveMember,
}) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const { results, loading } = useUserSearch(searchQuery);
  const [mobileSelectedForDelete, setMobileSelectedForDelete] = useState(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      setSearchQuery("");
      setMobileSelectedForDelete(null);
    }

    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const formattedResults = useMemo(() => {
    if (!results || results.length === 0) return [];
    return results.filter((u) => canJoinCastOrCrew(u)).map((u) => {
      const isCast = currentCast.some(c => String(c.id || c._id) === String(u._id));
      const isCrew = currentCrew.some(c => String(c.id || c._id) === String(u._id));
      const isOwner = String(u._id) === String(ownerId);

      let imageUrl = u.picture || "";
      if (imageUrl && !imageUrl.startsWith('http')) imageUrl = `${process.env.REACT_APP_AWS_URL}/${imageUrl}`;

      return {
        ...u,
        id: u._id,
        name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || t('Unknown User'),
        role: u.role || t('User'),
        image: imageUrl,
        alreadyAdded: isCast || isCrew || isOwner,
      };
    });
  }, [results, currentCast, currentCrew, ownerId, t]);

  const handleAddClick = (user) => {
    const roleLower = (user.role || "").toLowerCase();
    const category = roleLower.includes('actor') ? 'cast' : 'crew';
    if (onAddMember) onAddMember(user, category);
  };

  const handleMobileCardClick = (e, id) => {
    e.stopPropagation();
    setMobileSelectedForDelete(prev => prev === id ? null : id);
  };

  if (!isOpen) return null;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-bg-[#190033]/90 tw-backdrop-blur-md md:tw-p-4">
      {/* MOBILE FULL-SCREEN VIEW */}
      <div
        className="tw-flex md:tw-hidden tw-flex-col tw-w-full tw-h-full tw-bg-[#1F0439] tw-overflow-y-auto custom-scrollbar tw-p-6 tw-pt-12 tw-relative"
        onClick={() => setMobileSelectedForDelete(null)}
      >

        <button onClick={onClose} className="tw-absolute tw-top-6 tw-right-6 tw-text-[#FF43A7] hover:tw-bg-[#371E51] tw-p-2 tw-rounded-full tw-transition-all tw-bg-transparent tw-border-none tw-cursor-pointer tw-z-50">
          <FontAwesomeIcon icon={faXmark} className="tw-text-2xl" />
        </button>

        <h1 className="tw-font-bold tw-text-2xl tw-text-[#FF43A7] tw-tracking-tight tw-mb-8">{t("Cast & Crew")}</h1>

        {/* SEARCH SECTION */}
        <section className="tw-mb-10">
          <label className="tw-text-[#FFB0CF] tw-text-[10px] tw-uppercase tw-tracking-[0.2em] tw-mb-2 tw-block tw-ml-1">{t("Talent Discovery")}</label>
          <div className="tw-relative tw-w-full tw-mb-4" onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("Search talent pool by name...")}
              className="tw-w-full tw-bg-[#280D41] tw-border-2 tw-border-dashed tw-border-[#FF43A7]/40 tw-rounded-xl tw-px-5 tw-py-4 tw-text-white placeholder:tw-text-[#AA8894] focus:tw-outline-none focus:tw-border-[#FF43A7] tw-transition-all"
            />
            <FontAwesomeIcon icon={faSearch} className="tw-absolute tw-right-5 tw-top-1/2 -tw-translate-y-1/2 tw-text-[#FF43A7]" />
          </div>

          {formattedResults.length > 0 && (
            <div className="tw-bg-[#280D41] tw-rounded-2xl tw-p-1 tw-space-y-1" onClick={(e) => e.stopPropagation()}>
              {formattedResults.map((user) => (
                <div key={user.id} className="tw-flex tw-items-center tw-justify-between tw-p-3 hover:tw-bg-[#371E51] tw-rounded-xl tw-transition-colors">
                  <div className="tw-flex tw-items-center tw-gap-4">
                    <div className="tw-w-10 tw-h-10 tw-rounded-full tw-overflow-hidden tw-border tw-border-[#FFB0CF]/20 tw-bg-[#190033] tw-flex tw-items-center tw-justify-center">
                      {user.image ? <img src={user.image} alt={user.name} className="tw-w-full tw-h-full tw-object-cover" /> : <FontAwesomeIcon icon={faUser} className="tw-text-[#AA8894]/50" />}
                    </div>
                    <div>
                      <p className="tw-font-bold tw-text-white tw-text-sm">{user.name}</p>
                      <p className="tw-text-[10px] tw-text-[#DDB7FF]/70 tw-uppercase tw-tracking-wider">{t(user.role)}</p>
                    </div>
                  </div>
                  {user.alreadyAdded ? (
                     <span className="tw-text-[#FFB0CF]/50 tw-text-[10px] tw-uppercase tw-font-bold">{t("Added")}</span>
                  ) : (
                    <button onClick={() => handleAddClick(user)} className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-bg-[#FF43A7] tw-text-[#63003B] tw-border-none tw-shadow-[0_0_10px_rgba(255,67,167,0.3)] tw-cursor-pointer">
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* MOBILE CAST SECTION */}
        <section className="tw-mb-10">
          <div className="tw-flex tw-items-baseline tw-gap-3 tw-mb-4">
            <h2 className="tw-font-bold tw-text-2xl tw-tracking-tighter tw-text-white">{t("CAST")}</h2>
            <span className="tw-h-[2px] tw-flex-grow tw-bg-gradient-to-r tw-from-[#FF43A7]/30 tw-to-transparent"></span>
          </div>
          <div className="tw-flex tw-overflow-x-auto custom-scrollbar tw-gap-6 tw-py-2 -tw-mx-6 tw-px-6">
            {currentCast.map((member, idx) => {
              const uId = member.id || member._id || `cast-${idx}`;
              const isSelected = mobileSelectedForDelete === uId;

              return (
                <div key={idx} onClick={(e) => handleMobileCardClick(e, uId)} className="tw-flex-shrink-0 tw-w-20 tw-text-center tw-space-y-2 tw-relative tw-cursor-pointer">
                  <div className="tw-w-20 tw-h-20 tw-rounded-full tw-p-1 tw-bg-gradient-to-tr tw-from-[#FFB0CF] tw-to-[#DDB7FF]/20 tw-relative">
                    <div className="tw-w-full tw-h-full tw-rounded-full tw-overflow-hidden tw-border-2 tw-border-[#1F0439] tw-bg-[#371E51] tw-flex tw-items-center tw-justify-center">
                      {member.image ? <img src={member.image} alt={member.name} className="tw-w-full tw-h-full tw-object-cover" /> : <FontAwesomeIcon icon={faUser} className="tw-text-[#AA8894]/50 tw-text-xl" />}
                    </div>
                    {isSelected && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveMember && onRemoveMember(member, 'cast');
                          setMobileSelectedForDelete(null);
                        }}
                        className="tw-absolute tw-inset-1 tw-bg-red-600/90 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-z-20 tw-border-none tw-cursor-pointer hover:tw-bg-red-500 tw-transition-colors"
                      >
                        <FontAwesomeIcon icon={faTrashCan} className="tw-text-white tw-text-2xl" />
                      </button>
                    )}
                  </div>
                  <div className="tw-space-y-0.5">
                    <p className="tw-font-bold tw-text-xs tw-text-white tw-truncate">{member.name}</p>
                    <p className="tw-text-[9px] tw-text-[#FFB0CF] tw-uppercase tw-truncate">{t(member.role)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* MOBILE CREW SECTION */}
        <section className="tw-mb-10">
          <div className="tw-flex tw-items-baseline tw-gap-3 tw-mb-4">
            <h2 className="tw-font-bold tw-text-2xl tw-tracking-tighter tw-text-white">{t("CREW")}</h2>
            <span className="tw-h-[2px] tw-flex-grow tw-bg-gradient-to-r tw-from-[#DDB7FF]/30 tw-to-transparent"></span>
          </div>
          <div className="tw-flex tw-overflow-x-auto custom-scrollbar tw-gap-6 tw-py-2 -tw-mx-6 tw-px-6">
            {currentCrew.map((member, idx) => {
              const uId = member.id || member._id || `crew-${idx}`;
              const isSelected = mobileSelectedForDelete === uId;

              return (
                <div key={idx} onClick={(e) => handleMobileCardClick(e, uId)} className="tw-flex-shrink-0 tw-w-20 tw-text-center tw-space-y-2 tw-relative tw-cursor-pointer">
                  <div className="tw-w-20 tw-h-20 tw-rounded-full tw-p-1 tw-bg-gradient-to-tr tw-from-[#DDB7FF] tw-to-[#FFB0CF]/20 tw-relative">
                    <div className="tw-w-full tw-h-full tw-rounded-full tw-overflow-hidden tw-border-2 tw-border-[#1F0439] tw-bg-[#371E51] tw-flex tw-items-center tw-justify-center">
                      {member.image ? <img src={member.image} alt={member.name} className="tw-w-full tw-h-full tw-object-cover" /> : <FontAwesomeIcon icon={faUser} className="tw-text-[#AA8894]/50 tw-text-xl" />}
                    </div>
                    {isSelected && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveMember && onRemoveMember(member, 'crew');
                          setMobileSelectedForDelete(null);
                        }}
                        className="tw-absolute tw-inset-1 tw-bg-red-600/90 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-z-20 tw-border-none tw-cursor-pointer hover:tw-bg-red-500 tw-transition-colors"
                      >
                        <FontAwesomeIcon icon={faTrashCan} className="tw-text-white tw-text-2xl" />
                      </button>
                    )}
                  </div>
                  <div className="tw-space-y-0.5">
                    <p className="tw-font-bold tw-text-xs tw-text-white tw-truncate">{member.name}</p>
                    <p className="tw-text-[9px] tw-text-[#DDB7FF] tw-uppercase tw-truncate">{t(member.role)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

      </div>

      {/* DESKTOP MODAL VIEW */}
      <div className="tw-hidden md:tw-flex tw-relative tw-w-full tw-max-w-[1024px] tw-h-[640px] tw-bg-[#280D41] tw-border tw-border-[#5A3F49]/30 tw-shadow-[0_30px_60px_rgba(0,0,0,0.8)] tw-rounded-2xl tw-flex-row tw-overflow-hidden">

        <button onClick={onClose} className="tw-absolute tw-top-4 tw-right-4 tw-z-50 tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-bg-black/20 hover:tw-bg-[#FF43A7] tw-text-white tw-rounded-full tw-transition-colors tw-border-none tw-cursor-pointer">
          <FontAwesomeIcon icon={faXmark} />
        </button>

        {/* LEFT COLUMN: SEARCH */}
        <div className="tw-w-[40%] tw-h-full tw-p-8 tw-border-r tw-border-[#5A3F49]/20 tw-flex tw-flex-col">

          <div className="tw-flex tw-flex-col tw-mb-6">
            <span className="tw-text-[#FFB0CF] tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-mb-2">{t("Management Suite")}</span>
            <h1 className="tw-text-white tw-text-3xl tw-font-extrabold tw-tracking-tight tw-m-0">{t("Search Cast & Crew")}</h1>
          </div>

          <div className="tw-relative tw-w-full tw-mb-4">
            <div className="tw-absolute tw-left-5 tw-top-1/2 -tw-translate-y-1/2 tw-text-[#FFB0CF]/60">
              <FontAwesomeIcon icon={faSearch} />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={loading ? t("Loading users...") : t("Start typing name (min 2 chars)...")}
              className="tw-w-full tw-bg-[#190033] tw-border-2 tw-border-dashed tw-border-[#FFB0CF]/40 tw-rounded-xl tw-py-4 tw-pl-12 tw-pr-4 tw-text-white tw-text-base tw-outline-none placeholder:tw-text-[#AA8894]/60 focus:tw-border-[#FF43A7] tw-transition-colors disabled:tw-opacity-50"
            />
          </div>

          <div className="tw-flex-1 tw-overflow-y-auto custom-scrollbar tw-flex tw-flex-col tw-gap-3 tw-pr-2">
            {searchQuery && formattedResults.length === 0 && !loading && (
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-text-center tw-gap-4">
                <p className="tw-text-[#AA8894] tw-text-sm">{t("No users found matching")} "{searchQuery}"</p>
              </div>
            )}

            {formattedResults.map((user) => (
              <div key={user.id} className={`tw-flex tw-items-center tw-justify-between tw-p-3 tw-rounded-xl tw-border ${user.alreadyAdded ? 'tw-bg-[#371E51]/20 tw-border-transparent' : 'tw-bg-[#371E51]/40 tw-border-[#5A3F49]/20'}`}>
                <div className="tw-flex tw-items-center tw-gap-3">
                  <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-[#371E51] tw-border tw-border-white/10 tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-shrink-0">
                    {user.image ? <img src={user.image} alt={user.name} className="tw-w-full tw-h-full tw-object-cover" /> : <FontAwesomeIcon icon={faUser} className="tw-text-[#AA8894]/50" />}
                  </div>
                  <div className="tw-flex tw-flex-col">
                    <span className="tw-text-white tw-text-sm tw-font-bold">{user.name}</span>
                    <span className="tw-text-[#AA8894] tw-text-xs">{t(user.role)}</span>
                  </div>
                </div>

                {user.alreadyAdded ? (
                  <span className="tw-text-[#FFB0CF]/50 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-tight tw-px-2">{t("Added")}</span>
                ) : (
                  <button onClick={() => handleAddClick(user)} className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-[#FFB0CF] hover:tw-bg-[#FF43A7] tw-text-[#63003B] tw-flex tw-items-center tw-justify-center tw-shadow-[0_0_15px_rgba(255,67,167,0.4)] tw-transition-all tw-border-none tw-cursor-pointer">
                    <FontAwesomeIcon icon={faPlus} className="tw-text-sm" />
                  </button>
                )}
              </div>
            ))}
          </div>

        </div>

        {/* RIGHT COLUMN: CURRENT MEMBERS */}
        <div className="tw-w-[60%] tw-h-full tw-bg-[#190033]/50 tw-p-8 tw-flex tw-flex-col">

          <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
            <h2 className="tw-text-white tw-text-sm tw-font-extrabold tw-uppercase tw-tracking-[1.4px] tw-m-0">{t("Production Team")}</h2>
            <div className="tw-bg-[#FFB0CF]/20 tw-rounded-full tw-px-3 tw-py-0.5">
              <span className="tw-text-[#FFB0CF] tw-text-[11px] tw-font-bold tw-tracking-[1.4px]">{currentCast.length + currentCrew.length}</span>
            </div>
          </div>
          <div className="tw-w-12 tw-h-1 tw-bg-[#FFB0CF] tw-mb-8"></div>

          <div className="tw-flex tw-flex-1 tw-overflow-hidden">

            {/* CAST CONTAINER */}
            <div className="tw-w-1/2 tw-h-full tw-pr-4 tw-border-r tw-border-[#5A3F49]/20 tw-flex tw-flex-col">
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
                <FontAwesomeIcon icon={faUser} className="tw-text-[#DDB7FF] tw-text-xs" />
                <h3 className="tw-text-[#DDB7FF] tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-[2.2px] tw-m-0">{t("Cast")}</h3>
              </div>
              <div className="tw-flex-1 tw-overflow-y-auto custom-scrollbar tw-flex tw-flex-col tw-gap-3 tw-pr-2">
                {currentCast.map((actor, idx) => (
                  <div key={`d-cast-${idx}`} className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-[#371E51]/30 tw-rounded-xl tw-group">
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-[#371E51] tw-border tw-border-white/10 tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-shrink-0">
                        {actor.image ? <img src={actor.image} alt={actor.name} className="tw-w-full tw-h-full tw-object-cover" /> : <FontAwesomeIcon icon={faUser} className="tw-text-[#AA8894]/50" />}
                      </div>
                      <div className="tw-flex tw-flex-col">
                        <span className="tw-text-white tw-text-sm tw-font-bold">{actor.name}</span>
                        <span className="tw-text-[#AA8894] tw-text-[11px]">{actor.role ? t(actor.role) : t('Actor')}</span>
                      </div>
                    </div>
                    <button onClick={() => onRemoveMember && onRemoveMember(actor, 'cast')} className="tw-w-6 tw-h-6 tw-rounded-full tw-bg-[#AA8894]/20 hover:tw-bg-red-500 tw-text-[#AA8894] hover:tw-text-white tw-flex tw-items-center tw-justify-center tw-opacity-0 group-hover:tw-opacity-100 tw-transition-all tw-border-none tw-cursor-pointer"><FontAwesomeIcon icon={faXmark} className="tw-text-xs" /></button>
                  </div>
                ))}
                {currentCast.length === 0 && <p className="tw-text-[#AA8894] tw-text-xs tw-italic tw-mt-2">{t("No cast members added.")}</p>}
              </div>
            </div>

            {/* CREW CONTAINER */}
            <div className="tw-w-1/2 tw-h-full tw-pl-4 tw-flex tw-flex-col">
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
                <FontAwesomeIcon icon={faVideo} className="tw-text-[#DDB7FF] tw-text-xs" />
                <h3 className="tw-text-[#DDB7FF] tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-[2.2px] tw-m-0">{t("Crew")}</h3>
              </div>
              <div className="tw-flex-1 tw-overflow-y-auto custom-scrollbar tw-flex tw-flex-col tw-gap-3 tw-pr-2">
                {currentCrew.map((crewMember, idx) => (
                  <div key={`d-crew-${idx}`} className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-[#371E51]/30 tw-rounded-xl tw-group">
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-[#371E51] tw-border tw-border-white/10 tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-shrink-0">
                        {crewMember.image ? <img src={crewMember.image} alt={crewMember.name} className="tw-w-full tw-h-full tw-object-cover" /> : <FontAwesomeIcon icon={faUser} className="tw-text-[#AA8894]/50" />}
                      </div>
                      <div className="tw-flex tw-flex-col">
                        <span className="tw-text-white tw-text-sm tw-font-bold">{crewMember.name}</span>
                        <span className="tw-text-[#AA8894] tw-text-[11px] tw-capitalize">{crewMember.role ? t(crewMember.role) : (crewMember.epkRole?.replace('_', ' ') || t('Crew'))}</span>
                      </div>
                    </div>
                    <button onClick={() => onRemoveMember && onRemoveMember(crewMember, 'crew')} className="tw-w-6 tw-h-6 tw-rounded-full tw-bg-[#AA8894]/20 hover:tw-bg-red-500 tw-text-[#AA8894] hover:tw-text-white tw-flex tw-items-center tw-justify-center tw-opacity-0 group-hover:tw-opacity-100 tw-transition-all tw-border-none tw-cursor-pointer"><FontAwesomeIcon icon={faXmark} className="tw-text-xs" /></button>
                  </div>
                ))}
                {currentCrew.length === 0 && <p className="tw-text-[#AA8894] tw-text-xs tw-italic tw-mt-2">{t("No crew members added.")}</p>}
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}
